import type { BreakpointSize } from '../Breakpoint';
import type { SpacingSize, SpacingValue } from './Spacing';

export type SpacingSizeResponsive = Partial<
  Record<BreakpointSize, SpacingSize | SpacingValue>
>;

export function isResponsiveSpacingSize(
  value: SpacingValue | SpacingSize | SpacingSizeResponsive
): value is SpacingSizeResponsive {
  return typeof value === 'object';
}

/**
 * A component extending this interface will have props to set the component’s
 * vertical spacings. See {@link SpacingProps} for more details.
 */
export interface VerticalSpacingProps {
  /**
   * Set the vertical spacing (top and bottom) around the element. `vSpacing`
   * will override `topSpacing` and `bottomSpacing`.
   */
  vSpacing?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the spacing from the top of the element. */
  topSpacing?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the spacing from the bottom of the element. */
  bottomSpacing?: SpacingValue | SpacingSize | SpacingSizeResponsive;
}

/**
 * A component extending this interface will have props to set the component’s
 * horizontal spacings. See {@link SpacingProps} for more details.
 */
export interface HorizontalSpacingProps {
  /**
   * Set the horizontal spacing (left and right) around the element. Will
   * override `leftSpacing` and `rightSpacing`.
   */
  hSpacing?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the spacing from the left side of the element. */
  leftSpacing?: SpacingValue | SpacingSize | SpacingSizeResponsive;
  /** Set the spacing from the right side of the element. */
  rightSpacing?: SpacingValue | SpacingSize | SpacingSizeResponsive;
}

/**
 * A component extending this interface will have props to set the spacings
 * around the component.
 *
 * - Spacing can be set for both vertical or horizontal direction, or for each
 *   direction individually.
 * - Spacing can be set for all breakpoints, or for each {@link Breakpoint}
 *   individually.
 * - Spacing values are defined in {@link SpacingValue}.
 * - Spacing sizes are defined in {@link SpacingSize}.
 *
 *   - **Note: These are being deprecated.**
 * - Depending from the component it extends, spacing would set the padding or the
 *   margin of an HTML element.
 *
 * @example
 *
 * ```tsx
 *   // Will set the "medium" spacing on all sides
 *   <SpacedComponent spacing="md" />
 *  // Will set a "medium" vertical spacing for the xs breakpoint and a "small" horizontal spacing
 *   <SpacedComponent hSpacing={{ xs: "md", lg: "xl" }} />
 *   // Will set a "medium" vertical spacing
 *   <SpacedComponent bottomSpacing={{ lg: "md" }} />
 * ```
 */
export interface SpacingProps
  extends VerticalSpacingProps, HorizontalSpacingProps {
  /**
   * Set the spacing (horizontal and vertical) around the element. Will override
   * `hSpacing` and `vSpacing`.
   */
  spacing?: SpacingValue | SpacingSize | SpacingSizeResponsive;
}

/** The CSS property that can set a spacing. */
export type SpacingCSSProperty =
  | 'padding'
  | 'margin'
  | 'gap'
  | 'row-gap'
  | 'column-gap';
